import { useEffect, useRef, useState } from "react";
import { Video, VideoOff, Users, Loader2, Maximize } from "lucide-react";
import { ICE_SERVERS } from "@/lib/webrtc";
import { api } from "@/lib/api";
import { ScoreBug } from "@/components/LiveStreamPlayer";

export default function BroadcastPanel({ game }) {
    const videoRef = useRef(null);
    const wrapRef = useRef(null);
    const wsRef = useRef(null);
    const streamRef = useRef(null);
    const peersRef = useRef({});
    const [live, setLive] = useState(false);
    const [starting, setStarting] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [cameraOn, setCameraOn] = useState(false);
    const [viewers, setViewers] = useState(0);
    const [error, setError] = useState("");
    const [liveGame, setLiveGame] = useState(game);
    const [zoomed, setZoomed] = useState(false);

    useEffect(() => {
        const t = setInterval(() => {
            api.get("/games", { params: { status: "live" } })
                .then((r) => {
                    const g = r.data.find((x) => x.id === game.id);
                    if (g) setLiveGame(g);
                })
                .catch(() => {});
        }, 5000);
        return () => clearInterval(t);
    }, [game.id]);

    useEffect(() => () => stopAll(), []);

    const stopAll = () => {
        Object.values(peersRef.current).forEach((pc) => pc.close());
        peersRef.current = {};
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
            wsRef.current = null;
        }
        setLive(false);
        setConnecting(false);
        setStarting(false);
        setCameraOn(false);
        setViewers(0);
    };

    const createOffer = async (viewerId) => {
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        peersRef.current[viewerId] = pc;
        streamRef.current.getTracks().forEach((t) => pc.addTrack(t, streamRef.current));
        pc.onicecandidate = (e) => {
            if (e.candidate && wsRef.current?.readyState === 1)
                wsRef.current.send(JSON.stringify({ type: "ice", to: viewerId, data: e.candidate }));
        };
        let restarts = 0;
        pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === "failed" && restarts < 2 && pc.signalingState !== "closed") {
                restarts += 1;
                pc.restartIce();
                pc.createOffer({ iceRestart: true })
                    .then((o) => pc.setLocalDescription(o))
                    .then(() => {
                        if (wsRef.current?.readyState === 1)
                            wsRef.current.send(JSON.stringify({ type: "offer", to: viewerId, data: pc.localDescription }));
                    })
                    .catch(() => {});
            }
        };
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        wsRef.current?.send(JSON.stringify({ type: "offer", to: viewerId, data: offer }));
    };

    const start = async () => {
        setError("");
        if (!navigator.mediaDevices?.getUserMedia) {
            setError("Camera is not supported in this browser. Use a modern mobile browser on the HTTPS app URL.");
            return;
        }
        setStarting(true);
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: true,
            });
        } catch (err) {
            try {
                stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            } catch (err2) {
                setError("Camera/mic access denied or unavailable. Allow permissions and try again.");
                setStarting(false);
                return;
            }
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCameraOn(true);
        setStarting(false);
        setConnecting(true);

        const ws = new WebSocket(
            `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/stream/${game.id}?role=broadcaster`,
        );
        wsRef.current = ws;
        let opened = false;
        let failed = false;
        const failConnection = (msg) => {
            if (failed || opened) return;
            failed = true;
            clearTimeout(timeout);
            setError(msg);
            stopAll();
        };
        const timeout = setTimeout(() => {
            if (wsRef.current === ws && ws.readyState !== WebSocket.OPEN)
                failConnection("Could not reach the streaming server. Check your connection and try again.");
        }, 8000);
        ws.onopen = () => {
            opened = true;
            clearTimeout(timeout);
            setLive(true);
            setConnecting(false);
        };
        ws.onclose = () => {
            clearTimeout(timeout);
            setLive(false);
            setConnecting(false);
            if (!opened && wsRef.current === ws) failConnection("Streaming server closed the connection. Try again.");
        };
        ws.onerror = () =>
            failConnection("Could not reach the streaming server. Check your connection and try again.");
        ws.onmessage = async (e) => {
            const msg = JSON.parse(e.data);
            if (msg.type === "viewer-joined") await createOffer(msg.viewer);
            else if (msg.type === "viewer-left") {
                peersRef.current[msg.viewer]?.close();
                delete peersRef.current[msg.viewer];
            } else if (msg.type === "answer") {
                const pc = peersRef.current[msg.from];
                if (pc && !pc.remoteDescription) await pc.setRemoteDescription(msg.data).catch(() => {});
            } else if (msg.type === "ice") {
                const pc = peersRef.current[msg.from];
                if (pc?.remoteDescription) pc.addIceCandidate(msg.data).catch(() => {});
            } else if (msg.type === "viewer-count") setViewers(msg.count);
            else if (msg.type === "error") {
                setError(msg.message);
                stopAll();
            }
        };
    };

    const toggleFullscreen = () => {
        const el = wrapRef.current;
        if (!el) return;
        const zoomFallback = () => {
            videoRef.current?.play?.().catch(() => {});
            setZoomed(true);
            screen.orientation?.lock?.("landscape").catch(() => {});
        };
        if (zoomed) {
            setZoomed(false);
            screen.orientation?.unlock?.();
            return;
        }
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
            screen.orientation?.unlock?.();
        } else if (el.requestFullscreen) {
            el.requestFullscreen()
                .then(() => screen.orientation?.lock?.("landscape").catch(() => {}))
                .catch(zoomFallback);
        } else {
            zoomFallback();
        }
    };

    return (
        <div data-testid="broadcast-panel" className="space-y-4 mt-2">
            <div className="flex items-center justify-between text-xs text-slate-400">
                <span className="font-display uppercase tracking-widest">
                    {game.home_team?.short_name} vs {game.away_team?.short_name}
                    {game.week ? ` • Week ${game.week}` : ""}
                </span>
                <span
                    data-testid="broadcast-status"
                    className={`flex items-center gap-1.5 font-display font-bold uppercase tracking-widest ${
                        live ? "text-[#EF4444]" : "text-slate-500"
                    }`}
                >
                    {live && <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />}
                    {live ? "On Air" : "Off Air"}
                </span>
            </div>
            <div
                ref={wrapRef}
                className={
                    zoomed
                        ? "fixed inset-0 z-[100] bg-black"
                        : "aspect-video bg-black rounded-xl overflow-hidden border border-[#262638] relative"
                }
            >
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    data-testid="broadcast-preview"
                    className="w-full h-full object-cover"
                />
                {!cameraOn && !starting && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#08080C]/80">
                        <VideoOff className="w-10 h-10 text-slate-600" />
                        <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Camera off</p>
                    </div>
                )}
                {starting && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#08080C]/80">
                        <Loader2 className="w-8 h-8 text-[#FF5500] animate-spin" />
                        <p className="text-xs uppercase tracking-[0.25em] text-slate-300 font-semibold">Starting camera…</p>
                    </div>
                )}
                {connecting && cameraOn && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 bg-[#08080C]/85 rounded-full px-3 py-1.5">
                        <Loader2 className="w-3.5 h-3.5 text-[#FF5500] animate-spin" />
                        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-300 font-semibold">
                            Connecting to server…
                        </span>
                    </div>
                )}
                <button
                    data-testid="broadcast-fullscreen-button"
                    onClick={toggleFullscreen}
                    className="absolute top-3 right-3 p-2 rounded-lg bg-[#08080C]/85 text-slate-300 hover:text-white border border-[#262638] transition-colors duration-200"
                    aria-label="Fullscreen preview"
                >
                    <Maximize className="w-4 h-4" />
                </button>
                {live && <ScoreBug game={liveGame} />}
            </div>
            {error && (
                <p data-testid="broadcast-error" className="text-[#EF4444] text-sm">
                    {error}
                </p>
            )}
            <div className="flex items-center justify-between">
                <span data-testid="broadcast-viewer-count" className="flex items-center gap-2 text-xs text-slate-400">
                    <Users className="w-4 h-4 text-[#FFB800]" /> {viewers} watching
                </span>
                {live ? (
                    <button
                        data-testid="broadcast-stop-button"
                        onClick={stopAll}
                        className="flex items-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                    >
                        <VideoOff className="w-4 h-4" /> Stop Broadcast
                    </button>
                ) : (
                    <button
                        data-testid="broadcast-start-button"
                        onClick={start}
                        disabled={starting || connecting}
                        className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                    >
                        <Video className="w-4 h-4" /> Go Live
                    </button>
                )}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
                Open this page on your phone, tap Go Live, allow camera & mic, and point the rear camera at the court.
                Rotate your phone sideways for a wide landscape view, and use the fullscreen icon for a clean shot.
                Fans watch instantly on the Live page.
            </p>
        </div>
    );
}
